import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { ChevronRight } from "lucide-react";

const HERO_IMGS = [
  "https://images.pexels.com/photos/24602233/pexels-photo-24602233.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1615966650071-855b15f29ad1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxjb3VwbGUlMjB3YXJtJTIwc3Vuc2V0JTIwY2luZW1hdGljfGVufDB8fHx8MTc4NDcwMjE5Nnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1671716784499-a3d26826d844?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxzb2Z0JTIwd2FybSUyMGFic3RyYWN0JTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODQ3MDIxOTZ8MA&ixlib=rb-4.1.0&q=85",
];

const VALUES = ["Kindness", "Honesty", "Loyalty", "Growth", "Family", "Adventure", "Faith", "Health", "Creativity", "Ambition", "Compassion", "Gratitude", "Learning", "Independence", "Humour", "Optimism"];

const STEPS = [
  { id: "welcome", title: "Welcome to Wholesome", subtitle: "Instead of asking what you look like, we ask who you are.", type: "welcome" },
  { id: "story", title: "Who are you, really?", subtitle: "In a few sentences, tell us your story.", field: "story", type: "textarea", placeholder: "I'm a nurse, a dog mum, a slow reader. I grew up by the ocean and I've never lost my sense of wonder…" },
  { id: "smile", title: "What makes you smile?", subtitle: "Small joys tell us a lot about a person.", field: "happy_place", type: "text", placeholder: "Sunday markets, my grandmother's laugh, a good cup of coffee…" },
  { id: "grateful", title: "What are you grateful for today?", subtitle: "Your first entry in your gratitude journal.", field: "_grat", type: "text", placeholder: "The morning walk, and coffee that was still warm…" },
  { id: "dreams", title: "What are your dreams?", subtitle: "Where would you like the next chapters to go?", field: "dreams", type: "textarea", placeholder: "Build a home near the coast, travel slowly, love loudly…" },
  { id: "values", title: "What matters to you most?", subtitle: "Choose 3–6 values you'd never compromise on.", field: "values", type: "multi", options: VALUES },
  { id: "relationship", title: "What are you seeking?", subtitle: "Be honest — clarity is a kindness.", field: "relationship_goals", type: "select", options: ["Life partner", "Serious long-term", "Someone to grow with"] },
  { id: "children", title: "Children?", subtitle: "There's a right answer only for you.", field: "children", type: "select", options: ["I have and want more", "I have and don't want more", "None yet, want them", "Not sure", "Prefer no children"] },
  { id: "love_language", title: "Your love language?", subtitle: "How love feels most real to you.", field: "love_language", type: "select", options: ["Words of affirmation", "Quality time", "Acts of service", "Physical touch", "Gifts"] },
  { id: "adventure", title: "How adventurous are you?", subtitle: "From cosy at home to always exploring.", field: "adventure_level", type: "scale" },
  { id: "communication", title: "How do you communicate?", subtitle: "Especially when things get hard.", field: "communication", type: "select", options: ["Talk it through calmly", "Take space, then talk", "Direct and quick", "Write it out first"] },
  { id: "motto", title: "Your personal motto?", subtitle: "A line you live by.", field: "personal_motto", type: "text", placeholder: "Be kind, always — everyone's carrying something." },
];

export default function Onboarding() {
  const { refresh, user } = useAuth();
  const nav = useNavigate();
  const [i, setI] = useState(0);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const step = STEPS[i];
  const progress = ((i + 1) / STEPS.length) * 100;

  useEffect(() => { if (user?.onboarded) nav("/app"); }, [user, nav]);

  async function next() {
    setErr("");
    // Local update
    if (step.type === "welcome") return setI(i + 1);
    if (step.id === "grateful") {
      const g = vals._grat?.trim();
      if (g) {
        try { await api.post("/gratitude", { entry: g }); } catch (e) { setErr(formatApiError(e)); return; }
      }
      return setI(i + 1);
    }
    if (i === STEPS.length - 1) {
      // Final submit
      setBusy(true);
      try {
        const payload = { ...vals };
        delete payload._grat;
        if (payload.adventure_level) payload.adventure_level = Number(payload.adventure_level);
        await api.put("/profile", payload);
        await refresh();
        nav("/app");
      } catch (e) {
        setErr(formatApiError(e));
      } finally { setBusy(false); }
    } else {
      setI(i + 1);
    }
  }

  function skip() { setI((v) => Math.min(v + 1, STEPS.length - 1)); }

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img src={HERO_IMGS[i % HERO_IMGS.length]} alt="" className="w-full h-full object-cover opacity-40 transition-opacity duration-700" />
        <div className="absolute inset-0 bg-cream-100/70" />
      </div>
      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between">
          <Logo />
          <button data-testid="ob-skip" onClick={skip} className="text-xs text-navy/60 hover:text-navy">Skip</button>
        </div>
        <div className="mt-6 h-1 rounded-full bg-white/60 overflow-hidden">
          <div className="h-full bg-rose transition-all duration-700" style={{ width: `${progress}%` }} />
        </div>

        <div key={step.id} className="mt-16 reveal">
          <h1 className="font-display text-4xl md:text-5xl text-navy leading-tight" data-testid={`ob-title-${step.id}`}>{step.title}</h1>
          <p className="mt-3 text-navy/70">{step.subtitle}</p>

          <div className="mt-8">
            {step.type === "welcome" && (
              <div className="rounded-3xl glass p-6 text-navy/80">
                <p>We'll ask you a few gentle questions to build your life profile. There are no wrong answers.</p>
              </div>
            )}
            {step.type === "text" && (
              <input data-testid={`ob-${step.field}`} value={vals[step.field] || ""} onChange={(e) => setVals((v) => ({ ...v, [step.field]: e.target.value }))}
                placeholder={step.placeholder} className="w-full px-5 py-4 rounded-2xl bg-white border border-navy/10 text-lg" />
            )}
            {step.type === "textarea" && (
              <textarea data-testid={`ob-${step.field}`} value={vals[step.field] || ""} onChange={(e) => setVals((v) => ({ ...v, [step.field]: e.target.value }))}
                placeholder={step.placeholder} rows={5} className="w-full px-5 py-4 rounded-2xl bg-white border border-navy/10 text-lg leading-relaxed" />
            )}
            {step.type === "select" && (
              <div className="grid gap-2">
                {step.options.map((o) => (
                  <button key={o} data-testid={`ob-${step.field}-${o}`}
                    onClick={() => setVals((v) => ({ ...v, [step.field]: o }))}
                    className={`px-5 py-4 rounded-2xl text-left border transition-all duration-500 ${
                      vals[step.field] === o ? "bg-navy text-cream-100 border-navy" : "bg-white/70 border-navy/10 hover:bg-white"
                    }`}>
                    {o}
                  </button>
                ))}
              </div>
            )}
            {step.type === "multi" && (
              <div className="flex flex-wrap gap-2">
                {step.options.map((o) => {
                  const arr = vals[step.field] || [];
                  const on = arr.includes(o);
                  return (
                    <button key={o} data-testid={`ob-value-${o}`} onClick={() => {
                      const cur = new Set(arr);
                      on ? cur.delete(o) : cur.add(o);
                      setVals((v) => ({ ...v, [step.field]: Array.from(cur) }));
                    }} className={`px-4 py-2 rounded-full text-sm border transition-all duration-500 ${
                      on ? "bg-rose text-white border-rose" : "bg-white/70 border-navy/10 text-navy hover:bg-white"
                    }`}>{o}</button>
                  );
                })}
              </div>
            )}
            {step.type === "scale" && (
              <div>
                <div className="flex items-center justify-between gap-2">
                  {[1, 2, 3, 4, 5].map((n) => {
                    const on = Number(vals[step.field]) === n;
                    return (
                      <button key={n} data-testid={`ob-scale-${n}`} onClick={() => setVals((v) => ({ ...v, [step.field]: n }))}
                        className={`flex-1 py-6 rounded-2xl font-display text-3xl transition-all duration-500 ${
                          on ? "bg-navy text-cream-100" : "bg-white/70 text-navy hover:bg-white"
                        }`}>{n}</button>
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-navy/60 mt-2"><span>Cosy at home</span><span>Always exploring</span></div>
              </div>
            )}
          </div>

          {err && <div className="mt-4 text-sm text-rose-600" data-testid="ob-error">{err}</div>}

          <div className="mt-10 flex items-center justify-between">
            <button data-testid="ob-back" disabled={i === 0} onClick={() => setI(i - 1)}
              className="text-sm text-navy/60 disabled:opacity-30">Back</button>
            <button data-testid="ob-next" onClick={next} disabled={busy}
              className="btn-pill inline-flex items-center gap-2 bg-rose text-white hover:bg-rose-600 hover:scale-[1.02] disabled:opacity-60">
              {i === STEPS.length - 1 ? (busy ? "Finishing..." : "Enter Wholesome") : "Continue"} <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
